import { useEffect, useState } from 'react';
import { listVirtualUsers, type VirtualUser } from '../../api/virtualUser';
import { ComposeGallery } from './ComposeGallery';
import { Header, type AppView } from '../layout/Header';
import { TaggingPage } from '../tagging/TaggingPage';
import { BodyPage } from '../body/BodyPage';
import { DressPage } from '../dress/DressPage';
import { LocatePage } from '../locate/LocatePage';
import { BrandPage } from '../brand/BrandPage';
import { AppearancePage } from '../appearance/AppearancePage';

function useStandalonePanel() {
  try {
    return new URLSearchParams(window.location.search).get('panel') === 'wardrobe';
  } catch {
    return false;
  }
}

export function ComposeShell() {
  const [view, setView] = useState<AppView>('dress');
  const [virtualUsers, setVirtualUsers] = useState<VirtualUser[]>([]);
  const [selected, setSelected] = useState<VirtualUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const standalone = useStandalonePanel();

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listVirtualUsers();
      const items = res.items ?? [];
      setVirtualUsers(items);
      if (items[0]) setSelected(items[0]);
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载虚拟用户失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg)]">
        <div className="text-[var(--muted)]">加载虚拟用户...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[var(--bg)] p-6">
        <div className="surface max-w-md w-full p-6 text-center">
          <div className="text-sm text-[var(--red)] mb-2">加载失败</div>
          <div className="text-xs text-[var(--muted)] mb-4 break-all">{error}</div>
          <button onClick={load} className="btn-primary text-sm">
            重试
          </button>
        </div>
      </div>
    );
  }

  if (standalone) {
    return (
      <main className="h-screen overflow-hidden">
        <DressPage virtualUsers={virtualUsers} selected={selected} onSelect={setSelected} />
      </main>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header view={view} onChangeView={setView} />
      <main className="flex-1 overflow-hidden h-[calc(100vh-3.5rem)]">
        {view === 'tagging' && (
          <TaggingPage virtualUsers={virtualUsers} selected={selected} onSelect={setSelected} />
        )}
        {view === 'body' && (
          <BodyPage virtualUsers={virtualUsers} selected={selected} onSelect={setSelected} />
        )}
        {view === 'dress' && (
          <DressPage virtualUsers={virtualUsers} selected={selected} onSelect={setSelected} />
        )}
        {view === 'gallery' && <ComposeGallery />}
        {view === 'locate' && <LocatePage />}
        {view === 'brand' && <BrandPage />}
        {view === 'appearance' && <AppearancePage />}
      </main>
    </div>
  );
}
